import type { AutomationId } from "@repo/app-types/entities/automation";
import type {
  AutomationRunDb,
  AutomationRunId,
} from "@repo/app-types/entities/automationRun";
import type { UIMessage } from "ai";
import {
  getTagsInvalidatedByCreate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const automationRunApi = api.injectEndpoints({
  endpoints: (builder) => ({
    automationRuns: builder.query<
      AutomationRunDb[],
      AutomationId,
      { data: { automation_runs: AutomationRunDb[] } }
    >({
      query: (automationId) =>
        `/automations/${encodeURIComponent(automationId)}/runs`,
      transformResponse: (response) => response.data.automation_runs,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.AutomationRun, result),
    }),
    automationRunById: builder.query<
      AutomationRunDb,
      { automationId: AutomationId; automationRunId: AutomationRunId },
      { data: { automation_run: AutomationRunDb } }
    >({
      query: ({ automationId, automationRunId }) =>
        `/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(automationRunId)}`,
      transformResponse: (response) => response.data.automation_run,
      providesTags: (_result, _error, { automationRunId }) =>
        getTagsProvidedByEntity(TagType.AutomationRun, automationRunId),
    }),
    createPreviewAutomationRun: builder.mutation<
      AutomationRunDb,
      AutomationId,
      { data: { automation_run: AutomationRunDb } }
    >({
      query: (automationId) => ({
        method: "POST",
        url: `/automations/${encodeURIComponent(automationId)}/runs/preview`,
      }),
      transformResponse: (response) => response.data.automation_run,
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.AutomationRun),
    }),
    // Cancels a run: a queued run settles as canceled immediately; an active
    // one is flagged and the worker aborts it (the run's live stream then
    // delivers the terminal status).
    cancelAutomationRun: builder.mutation<
      AutomationRunDb,
      { automationId: AutomationId; automationRunId: AutomationRunId },
      { data: { automation_run: AutomationRunDb } }
    >({
      query: ({ automationId, automationRunId }) => ({
        method: "POST",
        url: `/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(automationRunId)}/cancel`,
      }),
      transformResponse: (response) => response.data.automation_run,
      invalidatesTags: (_result, _error, { automationRunId }) =>
        getTagsProvidedByEntity(TagType.AutomationRun, automationRunId),
    }),
    // One agent thread's static transcript: the run's orchestrator (threadId
    // === runId — the terminal run-detail page derives its progress rows from
    // its tool calls) or one subagent (threadId `<runId>:<managerId>`).
    automationRunThreadMessages: builder.query<
      UIMessage[],
      {
        automationId: AutomationId;
        automationRunId: AutomationRunId;
        threadId: string;
      },
      { data: { messages: UIMessage[] } }
    >({
      query: ({ automationId, automationRunId, threadId }) =>
        `/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(automationRunId)}/threads/${encodeURIComponent(threadId)}/messages`,
      transformResponse: (response) => response.data.messages,
      providesTags: (_result, _error, { automationRunId }) =>
        getTagsProvidedByEntity(TagType.AutomationRun, automationRunId),
    }),
  }),
});

export const {
  useAutomationRunsQuery,
  useAutomationRunByIdQuery,
  useCreatePreviewAutomationRunMutation,
  useCancelAutomationRunMutation,
  useAutomationRunThreadMessagesQuery,
} = automationRunApi;
